import {
    ApprovedLockError,
    PersistenceRequestError,
    StaleVersionError,
} from '../../persistence/PersistenceErrors';
import type {
    ConnectorDatasetId,
    PatientPersistenceRef,
} from '../../persistence/PersistenceRef';
import type {
    PersistedEntityId,
    PersistenceRefReader,
} from '../../persistence/PersistenceRegistry';
import type { SaveEffect, SaveEntryResult } from './SaveEffect';
import type { SaveEntityKind, SaveOutcome } from './SaveOutcome';
import type { SealedSaveIntent } from './SealedSaveIntent';

/**
 * Which of a kind's entries one orchestrator stage carries. Exists for
 * the one cross-kind ordering constraint: image ROWS must be created
 * before the structure-set stage (a new set's skeleton references its
 * image series), while image DELETES must run leaf-last, after every
 * kind that may reference the series has left the database. The split
 * lives in the orchestrator's stage list, where the ordering is visible
 * in one place; savers just filter by it.
 */
export type SaveScope = 'upserts' | 'deletes' | 'all';

/**
 * The port every per-kind saver implements — the orchestrator's whole
 * knowledge of a dataset kind's network pass. Adding a kind costs one
 * implementation plus its stage-list entry; the orchestrator, report and
 * store are untouched.
 *
 * A saver is pure I/O over sealed data: its registry view is
 * read-only, and every session mutation its calls earn comes back as
 * that entry's {@link SaveEffect} list for the orchestrator to apply.
 *
 * The synchronous seal() side deliberately stays OFF this port: each
 * kind's seal has its own signature and payload type, and the
 * orchestrator must call them by name inside the no-await window.
 */
export interface DatasetSaver {
    readonly entity: SaveEntityKind;
    saveEntries(
        sealed: SealedSaveIntent,
        patientRef: PatientPersistenceRef,
        overwrite: ReadonlySet<string> | null,
        scope: SaveScope
    ): Promise<SaveEntryResult[]>;
}

/** Whether an entry of `status` belongs to a stage of `scope`. */
export function inScope(
    status: 'new' | 'modified' | 'deleted',
    scope: SaveScope
): boolean {
    return scope === 'all' || (scope === 'deletes') === (status === 'deleted');
}

/** UI-facing noun per kind, for messages and redaction-safe logs. */
const NOUN: Record<SaveEntityKind, string> = {
    structureSet: 'structure set',
    registration: 'registration',
    image: 'image',
    dose: 'dose',
};

/**
 * The one error contract every saver's per-entry pass shares: a stale
 * If-Match token and the approved-lock refusal surface as conflicts (the
 * dialog's reload/overwrite decisions), everything else as that entry's
 * failure. Nothing escapes — one entry's throw never blocks another
 * entry or another kind.
 */
export async function mapSaveErrors(
    entity: SaveEntityKind,
    id: string,
    body: () => Promise<SaveOutcome>
): Promise<SaveOutcome> {
    try {
        return await body();
    } catch (error) {
        if (error instanceof StaleVersionError) {
            return {
                kind: 'conflict',
                entity,
                id,
                reason: 'staleVersion',
                ...(error.afterInterruptedAttempt && {
                    afterInterruptedAttempt: true,
                }),
            };
        }
        if (error instanceof ApprovedLockError) {
            return {
                kind: 'conflict',
                entity,
                id,
                reason: 'approvedOnServer',
            };
        }
        const message = error instanceof Error ? error.message : 'Save failed';
        // Redaction note: persistence errors carry static/status text
        // only; nothing here includes patient identifiers. The noun is a
        // kind label, never a dataset name.
        const noun = NOUN[entity];
        console.error(
            `${noun[0].toUpperCase()}${noun.slice(1)} save failed:`,
            message
        );
        return { kind: 'failed', entity, id, message };
    }
}

/**
 * DELETE for one dataset row, with the shared idempotency contract.
 *
 * A 404 FROM THE CONNECTOR means the row is already gone — another
 * session deleted it, or a previous attempt succeeded and failed to
 * record it. The desired end state holds, so this is a success:
 * reporting failure would keep the entry and its ref, so every later
 * save re-issues the DELETE, 404s again, and shows the row as failed
 * until the user discards and reloads.
 *
 * The problem+json declaration is what makes it the CONNECTOR's answer.
 * A bare 404 — a reverse proxy that refuses DELETE, a misrouted request,
 * an older connector without the route — means the row is still there,
 * and clearing the pending delete on that would present a wrong result
 * as a valid one. It propagates to {@link mapSaveErrors} instead.
 */
export async function deleteDatasetRow(
    refs: PersistenceRefReader,
    entity: SaveEntityKind,
    id: PersistedEntityId,
    doDelete: (datasetId: ConnectorDatasetId) => Promise<void>,
    effects: SaveEffect[]
): Promise<SaveOutcome> {
    const ref = refs.getRef(id);
    if (!ref) {
        // Local-only deletions never enter the pending set; a missing
        // ref here is bookkeeping corruption. Fail loudly per entry.
        return {
            kind: 'failed',
            entity,
            id,
            message: `Deleted ${NOUN[entity]} has no database identity`,
        };
    }
    try {
        await doDelete(ref.datasetId);
    } catch (error) {
        if (
            !(
                error instanceof PersistenceRequestError &&
                error.status === 404 &&
                error.fromConnector
            )
        ) {
            throw error;
        }
    }
    effects.push({ kind: 'removeRef', id });
    return { kind: 'saved', entity, id };
}
